import { PublicKey } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';

import {
  fetchMarketStates,
  useFetchMarketState,
} from './use-fetch-market-state';

import { MAX_SOLANA_PUBKEYS_PER_CALL } from '@/constants/helpers';
import { program } from '@/constants/solana';
import { OrderbookState } from '@/types/contract';
import { compareData } from '@/utils/fetch.utils';

export const fetchOrderbookState = async (
  orderbook: PublicKey,
  size: number,
) => {
  if (size === 5000) {
    return await program.account.orderBook5000.fetch(orderbook);
  } else if (size === 20000) {
    return await program.account.orderBook20000.fetch(orderbook);
  } else if (size === 200) {
    return await program.account.orderBook200.fetch(orderbook);
  } else {
    return await program.account.orderBook.fetch(orderbook);
  }
};

export const useFetchOrderbookState = (id?: number) => {
  const { data: marketState } = useFetchMarketState(id);
  return useQuery({
    queryKey: ['orderbook-state', marketState?.orderbook],
    enabled: id !== undefined && !!marketState?.orderbook,
    refetchInterval: 2000,
    structuralSharing: compareData,
    queryFn: async () => {
      if (!marketState?.orderbook) return null;
      return await fetchOrderbookState(
        marketState.orderbook,
        marketState.orderbookSize,
      );
    },
  });
};

export async function fetchOrderbookStatesByIds(
  ids: number[],
): Promise<Record<number, OrderbookState | null>> {
  const marketStates = await fetchMarketStates(ids);

  type Item = { key: PublicKey; idx: number; id: number };
  const g200: Item[] = [];
  const g5000: Item[] = [];
  const g20000: Item[] = [];
  const gDefault: Item[] = [];

  ids.forEach((id, idx) => {
    const ms = marketStates[id];
    if (!ms?.orderbook) return;
    const size = ms.orderbookSize as number;
    const key = ms.orderbook as PublicKey;
    if (size === 200) g200.push({ key, idx, id });
    else if (size === 5000) g5000.push({ key, idx, id });
    else if (size === 20000) g20000.push({ key, idx, id });
    else gDefault.push({ key, idx, id });
  });

  const result: Record<number, OrderbookState | null> = {};

  const run = async (
    items: Item[],
    fetcher: (ks: PublicKey[]) => Promise<any[]>,
  ) => {
    for (let i = 0; i < items.length; i += MAX_SOLANA_PUBKEYS_PER_CALL) {
      const slice = items.slice(i, i + MAX_SOLANA_PUBKEYS_PER_CALL);
      const keys = slice.map((s) => s.key);
      const part = await fetcher(keys);
      for (let j = 0; j < slice.length; j++) {
        result[slice[j].id] = part[j] ?? null;
      }
    }
  };

  await run(g200, (ks) => program.account.orderBook200.fetchMultiple(ks));
  await run(g5000, (ks) => program.account.orderBook5000.fetchMultiple(ks));
  await run(g20000, (ks) => program.account.orderBook20000.fetchMultiple(ks));
  await run(gDefault, (ks) => program.account.orderBook.fetchMultiple(ks));

  return result as Record<number, OrderbookState>;
}

export const useFetchOrderbookStates = (
  ids: number[],
  refetchInterval = 2000,
) => {
  return useQuery({
    queryKey: ['orderbook-states'],
    enabled: ids.length > 0,
    refetchInterval,
    placeholderData: (prev) => prev,
    structuralSharing: compareData,
    queryFn: () => fetchOrderbookStatesByIds(ids),
  });
};
